// src/components/ExerciseLibrary.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import useDebounce from '../hooks/useDebounce';
import { useAuthContext } from '../context/AuthContext';
import {
  IoSearch,
  IoAddOutline,
  IoCloseOutline,
  IoPlayCircleOutline,
  IoImageOutline,
  IoPencilOutline,
  IoFitnessOutline,
} from 'react-icons/io5';
import toast from 'react-hot-toast';

// ── Category config ────────────────────────────────────────────
const CATEGORIES = [
  { label: 'Pecho',     color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  { label: 'Espalda',   color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  { label: 'Piernas',   color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  { label: 'Glúteos',   color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
  { label: 'Hombros',   color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  { label: 'Bíceps',    color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  { label: 'Tríceps',   color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { label: 'Core',      color: 'bg-teal-500/20 text-teal-300 border-teal-500/30' },
  { label: 'Cardio',    color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  { label: 'Full Body', color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
];

const getCategoryStyle = (cat) => {
  const found = CATEGORIES.find(c => c.label.toLowerCase() === (cat || '').toLowerCase());
  return found?.color || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
};

const getCategoryInitial = (cat) => (cat || '?').charAt(0).toUpperCase();

// ── Create Exercise Modal ──────────────────────────────────────
const CreateExerciseModal = ({ onCreated, onClose }) => {
  const [form, setForm] = useState({ name: '', category: '', description: '', video_url: '' });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/admin/exercises', {
        name: form.name,
        description: form.description,
        category: form.category,
        video_url: form.video_url,
      });
      toast.success('Ejercicio creado');
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el ejercicio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-white">Nuevo Ejercicio</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10">
            <IoCloseOutline size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nombre */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1.5 block">Nombre *</label>
            <input
              type="text"
              autoFocus
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Ej: Sentadilla con Barra"
              required
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-marizan-red transition-colors"
            />
          </div>

          {/* Categoría — chips */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-2 block">Categoría</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {CATEGORIES.map(c => (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => set('category', form.category === c.label ? '' : c.label)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    form.category === c.label
                      ? c.color + ' scale-105'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={form.category}
              onChange={e => set('category', e.target.value)}
              placeholder="O escribe una categoría personalizada..."
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1.5 block">Descripción</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Breve descripción de la ejecución..."
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors resize-none"
            />
          </div>

          {/* Video URL */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1.5 block">
              URL de Video <span className="text-gray-500 font-normal">(opcional)</span>
            </label>
            <input
              type="url"
              value={form.video_url}
              onChange={e => set('video_url', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="flex-1 py-2.5 rounded-xl bg-marizan-red hover:bg-red-700 text-white font-semibold text-sm transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Crear Ejercicio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Exercise Card ──────────────────────────────────────────────
const ExerciseCard = ({ exercise, isCoach }) => {
  const catStyle = getCategoryStyle(exercise.category);
  const initial = getCategoryInitial(exercise.category);
  const hasVideo = !!exercise.video_url;
  const hasImages = exercise.images?.length > 0;

  return (
    <div className="glass-panel rounded-xl overflow-hidden group hover:-translate-y-1 hover:border-white/20 transition-all duration-300 flex flex-col">
      {/* Color strip header */}
      <div className={`h-1.5 w-full ${catStyle.split(' ')[0].replace('/20', '/60')}`} />

      <div className="p-4 flex flex-col flex-1">
        {/* Top row: initial + name + edit */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold flex-shrink-0 border ${catStyle}`}>
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/exercises/${exercise.id}`}>
              <h3 className="font-semibold text-white group-hover:text-marizan-red transition-colors leading-tight line-clamp-2">
                {exercise.name}
              </h3>
            </Link>
            {exercise.category && (
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border ${catStyle}`}>
                {exercise.category}
              </span>
            )}
          </div>
          {isCoach && (
            <Link
              to={`/exercises/${exercise.id}/edit`}
              className="flex-shrink-0 p-2 rounded-lg bg-white/8 text-gray-300 hover:text-white hover:bg-marizan-red/70 border border-white/10 hover:border-marizan-red/40 transition-all"
              title="Editar ejercicio"
            >
              <IoPencilOutline size={15} />
            </Link>
          )}
        </div>

        {/* Description */}
        {exercise.description && (
          <p className="text-xs text-gray-400 line-clamp-2 flex-1 mb-3">{exercise.description}</p>
        )}

        {/* Footer indicators */}
        <div className="flex items-center gap-2 mt-auto pt-2 border-t border-white/5">
          {hasVideo && (
            <span className="flex items-center gap-1 text-xs text-blue-400">
              <IoPlayCircleOutline size={14} /> Video
            </span>
          )}
          {hasImages && (
            <span className="flex items-center gap-1 text-xs text-purple-400">
              <IoImageOutline size={14} /> {exercise.images.length} foto{exercise.images.length !== 1 ? 's' : ''}
            </span>
          )}
          {!hasVideo && !hasImages && (
            <span className="text-xs text-gray-600">Sin multimedia</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────
const ExerciseLibrary = () => {
  const { user } = useAuthContext();
  const isCoach = ['coach', 'admin'].includes(user?.role);

  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get('/admin/exercises', { params });
      setExercises(res.data);
    } catch {
      toast.error('Error al cargar ejercicios');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter]);

  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const handleCreated = (ex) => {
    setExercises(prev => [...prev, ex].sort((a, b) => a.name.localeCompare(b.name)));
  };

  // All categories present in loaded exercises
  const usedCategories = [...new Set(exercises.map(e => e.category).filter(Boolean))].sort();

  return (
    <div className="fade-up">
      {isModalOpen && <CreateExerciseModal onCreated={handleCreated} onClose={() => setIsModalOpen(false)} />}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-1">
            Biblioteca de Ejercicios
          </h1>
          <p className="text-gray-400 text-sm">
            {loading ? '…' : `${exercises.length} ejercicio${exercises.length !== 1 ? 's' : ''}`}
            {categoryFilter && ` · ${categoryFilter}`}
          </p>
        </div>
        {isCoach && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-colors flex-shrink-0"
          >
            <IoAddOutline size={20} />
            <span className="hidden sm:inline">Nuevo</span>
          </button>
        )}
      </div>

      {/* Search + category chips */}
      <div className="space-y-3 mb-6">
        <div className="relative">
          <IoSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar ejercicio..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-marizan-red transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <IoCloseOutline size={18} />
            </button>
          )}
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('')}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
              categoryFilter === ''
                ? 'bg-white text-black border-white'
                : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'
            }`}
          >
            Todas
          </button>
          {usedCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                categoryFilter === cat
                  ? getCategoryStyle(cat)
                  : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-40 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : exercises.length === 0 ? (
        <div className="text-center border-2 border-dashed border-gray-700 rounded-2xl py-16 px-8">
          <IoFitnessOutline className="text-5xl text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">
            {searchTerm || categoryFilter ? 'Sin resultados para los filtros actuales' : 'No hay ejercicios aún'}
          </p>
          {(searchTerm || categoryFilter) && (
            <button
              onClick={() => { setSearchTerm(''); setCategoryFilter(''); }}
              className="mt-3 text-sm text-marizan-red hover:text-red-400 transition-colors"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {exercises.map(ex => (
            <ExerciseCard key={ex.id} exercise={ex} isCoach={isCoach} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExerciseLibrary;